import * as React from 'react'
import { CreateInvoiceDetail } from '../InvoiceDetailStore'
import { observer } from 'mobx-react'
import {
  CreateInvoiceLine,
  InvoiceStatus,
  ICompany,
  API,
  InvoiceProductCostHistory,
  INestedProduct,
  InvoiceProductCostHistoryFilter,
  IInvoiceLine,
} from '@getgreenline/homi-shared'
import { Popover, Button, Icon, message } from 'antd'
import { Currency } from '../../../../../components/Currency'
import { GrPrice, parseErrorMsg } from '../../../../../utilities/helpers'
import { CurrencyInput } from '../../../../../components/CurrencyInput'
import { CostPerUnitHistoryDrawer } from './CostPerUnitHistoryDrawer'

interface Props {
  company: ICompany
  createInvoice: CreateInvoiceDetail
  line: CreateInvoiceLine | IInvoiceLine
  selectedProduct?: INestedProduct
  isOverrideWeightedAverage: boolean
  isOverrideWeightedAverageButtonVisible: boolean
  getProduct: (productId: string) => void
  onEditCompletedCost: (line: CreateInvoiceLine) => void
}

interface State {
  isDrawerVisible: boolean
  costHistory: InvoiceProductCostHistory[] | undefined
  hasMore: boolean
}

@observer
export class CostPerUnit extends React.Component<Props, State> {
  costHistoryFilter = new InvoiceProductCostHistoryFilter()

  constructor(props: Props) {
    super(props)

    this.state = {
      isDrawerVisible: false,
      costHistory: undefined,
      hasMore: true,
    }
  }

  setIsDrawerVisible = (isDrawerVisible: boolean) => this.setState({ isDrawerVisible })

  showCostHistory = async (productId: string) => {
    this.setIsDrawerVisible(true)

    await this.props.getProduct(productId)

    if (this.costHistoryFilter.productId !== productId) {
      await this.setState({ costHistory: undefined })
    }

    await this.getCostHistory(productId)
  }

  getCostHistory = async (productId: string) => {
    try {
      const companyId = this.props.company.id

      if (this.costHistoryFilter.productId !== productId) {
        this.costHistoryFilter.setProductId(productId)
      }

      const costHistoryResponse = await API.getCostHistory(companyId, this.costHistoryFilter)

      this.setState({
        costHistory: [...(this.state.costHistory || []), ...costHistoryResponse.costHistory],
      })

      if (costHistoryResponse.costHistory.length === 0) {
        this.setState({ hasMore: false })
      } else {
        const nextOffset = this.costHistoryFilter.offset + this.costHistoryFilter.limit
        this.costHistoryFilter.setOffset(nextOffset)
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || `An error occured when loading the cost history`)
    }
  }

  get costPerUnit(): number {
    const { line } = this.props

    return line instanceof CreateInvoiceLine ? line.costPerUnit : line.total / (line.quantity || 1)
  }

  get linePrice(): number {
    return this.props.line.price || 0
  }

  get isCostPerUnitWarningVisible(): boolean {
    return this.costPerUnit !== 0 && this.costPerUnit > this.linePrice
  }

  get isEditableCostPerUnitVisible(): boolean {
    const { createInvoice, isOverrideWeightedAverage, company } = this.props

    const isNotCompleted = createInvoice.invoiceDetail?.status !== InvoiceStatus.COMPLETED

    const weightedAverageCase =
      (isNotCompleted || isOverrideWeightedAverage) &&
      company.inventoryCostOption === 'weightedAverage'

    const lastCostCase = company.inventoryCostOption === 'lastCost'

    return weightedAverageCase || lastCostCase
  }

  get popoverContent(): JSX.Element {
    const stringCostPerUnit = GrPrice.convertCentToDollar(this.costPerUnit)
    const stringRetailPrice = GrPrice.convertCentToDollar(this.linePrice)

    return (
      <div>
        <div>
          Your cost per unit (<b>{stringCostPerUnit}</b>) is higher than your retail price (
          <b>{stringRetailPrice}</b>).
        </div>
        <div>
          This will result in <b style={{ color: 'red' }}>negative</b> margins.
        </div>
      </div>
    )
  }

  renderEditableCostPerUnit = (line: CreateInvoiceLine): JSX.Element => {
    const { isOverrideWeightedAverageButtonVisible, onEditCompletedCost } = this.props

    const borderStyle = this.isCostPerUnitWarningVisible
      ? {
          border: '1px solid red',
          borderRadius: 5,
        }
      : undefined

    return (
      <>
        {isOverrideWeightedAverageButtonVisible && (
          <Button onClick={() => onEditCompletedCost(line)} style={borderStyle}>
            <Currency centValue={line.costPerUnit} />
            <Icon type='edit' />
          </Button>
        )}

        {this.isEditableCostPerUnitVisible && (
          <CurrencyInput
            className='gr-currency-input'
            centValue={line.costPerUnit}
            style={borderStyle}
            onChange={(value) => {
              line.costPerUnit = value || 0
              line.setTotal((line.quantity || 0) * line.costPerUnit)
              this.props.createInvoice.setIsEdited(true)
            }}
            onlyPositiveNumber
          />
        )}
      </>
    )
  }

  render() {
    const { createInvoice, line, selectedProduct } = this.props
    const { isDrawerVisible, hasMore, costHistory } = this.state
    const popoverProp = this.isCostPerUnitWarningVisible
      ? { defaultVisible: true }
      : { visible: false }

    return (
      <div className='d-flex align-items-center justify-content-end'>
        {createInvoice.isEditMode && line instanceof CreateInvoiceLine ? (
          <Popover title='Warning' placement='top' content={this.popoverContent} {...popoverProp}>
            <span>{this.renderEditableCostPerUnit(line)}</span>
          </Popover>
        ) : this.isCostPerUnitWarningVisible ? (
          <Popover title='Warning' placement='top' content={this.popoverContent}>
            <span className='cursor-pointer'>
              <Currency centValue={this.costPerUnit} style={{ color: 'red' }} />
            </span>
          </Popover>
        ) : (
          <Currency centValue={this.costPerUnit} />
        )}

        {line.productId !== null && (
          <Button
            icon='clock-circle'
            size='small'
            className='ml-2'
            onClick={() => this.showCostHistory(line.productId!)}
          />
        )}

        {isDrawerVisible && selectedProduct && (
          <CostPerUnitHistoryDrawer
            isDrawerVisible={isDrawerVisible}
            setIsDrawerVisible={this.setIsDrawerVisible}
            product={selectedProduct}
            costHistory={costHistory}
            getCostHistory={this.getCostHistory}
            hasMore={hasMore}
          />
        )}
      </div>
    )
  }
}
